import * as React from 'react';
import { StringResources } from 'src/assets/StringResources';
import { TextInput } from '../Shared/Input/TextInput';
import { copy, DeepPartial, WithoutTypename } from 'src/utils/utils';
import { noop } from 'src/utils/utils';
import { PhoneNumberTextInput } from '../Shared/Input/PhoneNumberTextInput';
import { InlineActionLink } from '../Shared/InlineActionLink';
import { gql } from '@apollo/client';
import { CustomerInfoViewFragment } from './__generated__/CustomerInfoView.generated';

export interface ICustomerInfoPageProps {
  validations: Partial<CustomerInfoViewFragment>;
  customer: WithoutTypename<DeepPartial<CustomerInfoViewFragment>>;
  setCustomer:
    | ((c: WithoutTypename<DeepPartial<CustomerInfoViewFragment>>) => void)
    | undefined;
  disabled?: boolean;
}

export const CustomerInfoView: React.FunctionComponent<
  ICustomerInfoPageProps
> = props => {
  const [customerInput, setCustomerInput] = React.useState(props.customer);
  const setCustomer = props.setCustomer || noop;
  const [isSecondaryPhoneNumberShown, setIsSecondaryPhoneNumberShown] =
    React.useState(Boolean(props.customer.secondaryPhoneNumber));
  const [isAddressExpanded, setIsAddressExpanded] = React.useState<boolean>(
    Boolean(props.customer.shippingAddress)
  );
  return (
    <div className='space-y-12'>
      <div className='space-y-7'>
        <PhoneNumberTextInput
          label={'Phone number'}
          value={customerInput.phoneNumber || ''}
          onChange={t => {
            const newPhoneNumber = copy(customerInput);
            newPhoneNumber.phoneNumber = t;
            setCustomerInput(newPhoneNumber);
            setCustomer(newPhoneNumber);
          }}
          errorMessage={props.validations.phoneNumber || undefined}
          disabled={props.disabled || !props.setCustomer}
        />
        <TextInput
          type={'text'}
          label={StringResources.firstName}
          value={customerInput.firstName || ''}
          onChange={t => {
            const newCustomer = copy(customerInput);
            newCustomer.firstName = t;
            setCustomerInput(newCustomer);
            setCustomer(newCustomer);
          }}
          errorMessage={props.validations.firstName || undefined}
          disabled={props.disabled || !props.setCustomer}
        />
        <TextInput
          type={'text'}
          label={StringResources.lastName}
          value={customerInput.lastName || ''}
          onChange={t => {
            const newCustomer = copy(customerInput);
            newCustomer.lastName = t;
            setCustomerInput(newCustomer);
            setCustomer(newCustomer);
          }}
          errorMessage={props.validations.lastName || undefined}
          disabled={props.disabled || !props.setCustomer}
        />

        <TextInput
          type={'text'}
          label={'Email'}
          value={customerInput.email || ''}
          onChange={t => {
            const newCustomer = copy(customerInput);
            newCustomer.email = t;
            setCustomerInput(newCustomer);
            setCustomer(newCustomer);
          }}
          errorMessage={props.validations.email || undefined}
          disabled={props.disabled || !props.setCustomer}
        />
        <TextInput
          type={'text'}
          label={'Company name'}
          value={customerInput.companyName || ''}
          onChange={t => {
            const newCustomer = copy(customerInput);
            newCustomer.companyName = t;
            setCustomerInput(newCustomer);
            setCustomer(newCustomer);
          }}
          errorMessage={props.validations.companyName || undefined}
          disabled={props.disabled || !props.setCustomer}
        />
        {isSecondaryPhoneNumberShown && (
          <PhoneNumberTextInput
            label={'Secondary phone number'}
            value={customerInput.secondaryPhoneNumber || ''}
            onChange={t => {
              const secondaryPhoneNumber = copy(customerInput);
              secondaryPhoneNumber.secondaryPhoneNumber = t;
              setCustomerInput(secondaryPhoneNumber);
              setCustomer(secondaryPhoneNumber);
            }}
            errorMessage={props.validations.secondaryPhoneNumber || undefined}
            disabled={props.disabled || !props.setCustomer}
          />
        )}
        {!isSecondaryPhoneNumberShown && (
          <InlineActionLink
            text={'Add secondary phone number'}
            onClick={() => {
              setIsSecondaryPhoneNumberShown(true);
            }}
          />
        )}
      </div>
      {/* {!isAddressExpanded && (
        <InlineActionLink
          text={'Add address'}
          onClick={() => {
            setIsAddressExpanded(true);
          }}
        />
      )} */}
      {isAddressExpanded && (
        <div className='space-y-7'>
          <TextInput
            type={'text'}
            label={'Address line 1'}
            value={customerInput.shippingAddress?.addressLine1 || ''}
            onChange={t => {
              const newAddress = copy(customerInput);
              if (!newAddress.shippingAddress) {
                newAddress.shippingAddress = {};
              }
              newAddress.shippingAddress.addressLine1 = t;
              setCustomerInput(newAddress);
              setCustomer(newAddress);
            }}
            errorMessage={undefined}
            disabled={props.disabled || !props.setCustomer}
          />

          <TextInput
            type={'text'}
            label={'Address line 2'}
            value={customerInput.shippingAddress?.addressLine2 || ''}
            onChange={t => {
              const newAddress = copy(customerInput);
              if (!newAddress.shippingAddress) {
                newAddress.shippingAddress = {};
              }
              newAddress.shippingAddress.addressLine2 = t;
              setCustomerInput(newAddress);
              setCustomer(newAddress);
            }}
            errorMessage={undefined}
            disabled={props.disabled || !props.setCustomer}
          />

          <TextInput
            type={'text'}
            label={'City'}
            value={customerInput.shippingAddress?.locality || ''}
            onChange={t => {
              const newAddress = copy(customerInput);
              if (!newAddress.shippingAddress) {
                newAddress.shippingAddress = newAddress.shippingAddress || {};
              }
              newAddress.shippingAddress.locality = t;
              setCustomerInput(newAddress);
              setCustomer(newAddress);
            }}
            errorMessage={undefined}
            disabled={props.disabled || !props.setCustomer}
          />
          <TextInput
            type={'text'}
            label={'Postal Code'}
            value={customerInput.shippingAddress?.postalCode || ''}
            onChange={t => {
              const newAddress = copy(customerInput);
              if (!newAddress.shippingAddress) {
                newAddress.shippingAddress = newAddress.shippingAddress || {};
              }
              newAddress.shippingAddress.postalCode = t;
              setCustomerInput(newAddress);
              setCustomer(newAddress);
            }}
            errorMessage={undefined}
            disabled={props.disabled || !props.setCustomer}
          />
          <TextInput
            type={'text'}
            label={'Country'}
            value={customerInput.shippingAddress?.country || ''}
            onChange={t => {
              const newAddress = copy(customerInput);
              if (!newAddress.shippingAddress) {
                newAddress.shippingAddress = newAddress.shippingAddress || {};
              }
              newAddress.shippingAddress.country = t;
              setCustomerInput(newAddress);
              setCustomer(newAddress);
            }}
            errorMessage={undefined}
            disabled={props.disabled || !props.setCustomer}
          />
        </div>
      )}
    </div>
  );
};

const fragment = gql`
  fragment CustomerInfoView on Customer {
    id
    firstName
    lastName
    phoneNumber
    email
    secondaryPhoneNumber
    shippingAddress {
      addressLine1
      addressLine2
      country
      locality
      postalCode
    }
    companyName
  }
`;
